import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";

import RightDrawer from "../../shared/components/RightDrawer";

const EMPTY_FORM = {
  type_name: "",
  type_code: "",
  description: "",
  is_permanent: false,
  default_probation_days: 90,
  default_notice_period_days: 30,
  status: "Active",
};

const EditEmploymentDrawer = ({
  isOpen = false,
  open = false,
  onClose,
  employmentType = null,
  onSave,
}) => {
  const drawerOpen = isOpen || open;

  const [form, setForm] =
    useState(EMPTY_FORM);

  const [saving, setSaving] =
    useState(false);

  const isEditMode =
    Boolean(employmentType);

  useEffect(() => {
    if (!drawerOpen) {
      return;
    }

    if (employmentType) {
      setForm({
        type_name:
          employmentType.type_name || "",
        type_code:
          employmentType.type_code || "",
        description:
          employmentType.description || "",
        is_permanent:
          Boolean(
            employmentType.is_permanent
          ),
        default_probation_days:
          employmentType
            .default_probation_days ?? 90,
        default_notice_period_days:
          employmentType
            .default_notice_period_days ?? 30,
        status:
          employmentType.status || "Active",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [drawerOpen, employmentType]);

  const handleChange = (
    field,
    value
  ) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.type_name.trim()) {
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...form,
        type_name: form.type_name.trim(),
        type_code: form.type_code
          .trim()
          .toUpperCase(),
        default_probation_days: Number(
          form.default_probation_days
        ),
        default_notice_period_days:
          Number(
            form.default_notice_period_days
          ),
      };

      if (typeof onSave === "function") {
        await onSave(
          payload,
          employmentType?.uuid || null
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (!drawerOpen) {
    return null;
  }

  return (
    <RightDrawer
      open={drawerOpen}
      onClose={onClose}
      title={
        isEditMode
          ? "Edit Employment Type"
          : "Add Employment Type"
      }
      description={
        isEditMode
          ? "Update employment type configuration."
          : "Create a new employment classification."
      }
      width="520px"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="
              h-10
              rounded-lg
              border
              border-slate-200
              bg-white
              px-4
              text-sm
              font-medium
              text-slate-600
              transition
              hover:bg-slate-50
              disabled:opacity-50
            "
          >
            Cancel
          </button>

          <button
            type="submit"
            form="employment-type-form"
            disabled={
              saving ||
              !form.type_name.trim()
            }
            className="
              flex
              h-10
              items-center
              gap-2
              rounded-lg
              bg-[#147db5]
              px-4
              text-sm
              font-medium
              text-white
              transition
              hover:bg-[#116d9d]
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
          >
            <Save
              size={16}
              strokeWidth={1.8}
            />

            <span>
              {saving
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Create Type"}
            </span>
          </button>
        </div>
      }
    >
      <form
        id="employment-type-form"
        onSubmit={handleSubmit}
        className="space-y-5 p-6"
      >
        {/* BASIC INFORMATION */}

        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            Basic Information
          </h3>

          <p className="mt-1 text-xs text-slate-400">
            Define the employment classification.
          </p>
        </div>

        {/* Type Name */}

        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-600">
            Employment Type Name
            <span className="ml-1 text-red-500">
              *
            </span>
          </label>

          <input
            type="text"
            value={form.type_name}
            onChange={(event) =>
              handleChange(
                "type_name",
                event.target.value
              )
            }
            placeholder="e.g. Full-Time"
            className="
              h-10
              w-full
              rounded-lg
              border
              border-slate-200
              px-3
              text-sm
              text-slate-700
              outline-none
              placeholder:text-slate-400
              focus:border-[#147db5]
              focus:ring-2
              focus:ring-[#147db5]/10
            "
          />
        </div>

        {/* Code */}

        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-600">
            Type Code
          </label>

          <input
            type="text"
            value={form.type_code}
            onChange={(event) =>
              handleChange(
                "type_code",
                event.target.value
              )
            }
            placeholder="e.g. FT"
            maxLength={20}
            className="
              h-10
              w-full
              rounded-lg
              border
              border-slate-200
              px-3
              text-sm
              uppercase
              text-slate-700
              outline-none
              placeholder:normal-case
              placeholder:text-slate-400
              focus:border-[#147db5]
              focus:ring-2
              focus:ring-[#147db5]/10
            "
          />
        </div>

        {/* Description */}

        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-600">
            Description
          </label>

          <textarea
            value={form.description}
            onChange={(event) =>
              handleChange(
                "description",
                event.target.value
              )
            }
            placeholder="Describe this employment type..."
            rows={4}
            className="
              w-full
              resize-none
              rounded-lg
              border
              border-slate-200
              px-3
              py-2.5
              text-sm
              text-slate-700
              outline-none
              placeholder:text-slate-400
              focus:border-[#147db5]
              focus:ring-2
              focus:ring-[#147db5]/10
            "
          />
        </div>

        {/* DEFAULT SETTINGS */}

        <div className="border-t border-slate-100 pt-5">
          <h3 className="text-sm font-semibold text-slate-800">
            Default Settings
          </h3>

          <p className="mt-1 text-xs text-slate-400">
            Configure default employment settings.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Probation */}

          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">
              Probation Days
            </label>

            <input
              type="number"
              min="0"
              value={
                form.default_probation_days
              }
              onChange={(event) =>
                handleChange(
                  "default_probation_days",
                  event.target.value
                )
              }
              className="
                h-10
                w-full
                rounded-lg
                border
                border-slate-200
                px-3
                text-sm
                text-slate-700
                outline-none
                focus:border-[#147db5]
                focus:ring-2
                focus:ring-[#147db5]/10
              "
            />
          </div>

          {/* Notice */}

          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">
              Notice Period Days
            </label>

            <input
              type="number"
              min="0"
              value={
                form.default_notice_period_days
              }
              onChange={(event) =>
                handleChange(
                  "default_notice_period_days",
                  event.target.value
                )
              }
              className="
                h-10
                w-full
                rounded-lg
                border
                border-slate-200
                px-3
                text-sm
                text-slate-700
                outline-none
                focus:border-[#147db5]
                focus:ring-2
                focus:ring-[#147db5]/10
              "
            />
          </div>
        </div>

        {/* Permanent */}

        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4">
          <input
            type="checkbox"
            checked={form.is_permanent}
            onChange={(event) =>
              handleChange(
                "is_permanent",
                event.target.checked
              )
            }
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#147db5] focus:ring-[#147db5]"
          />

          <span>
            <span className="block text-sm font-medium text-slate-700">
              Permanent Employment
            </span>

            <span className="mt-0.5 block text-xs leading-5 text-slate-400">
              Mark this employment type as a
              permanent classification.
            </span>
          </span>
        </label>

        {/* Status */}

        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-600">
            Status
          </label>

          <select
            value={form.status}
            onChange={(event) =>
              handleChange(
                "status",
                event.target.value
              )
            }
            className="
              h-10
              w-full
              rounded-lg
              border
              border-slate-200
              bg-white
              px-3
              text-sm
              text-slate-700
              outline-none
              focus:border-[#147db5]
              focus:ring-2
              focus:ring-[#147db5]/10
            "
          >
            <option value="Active">
              Active
            </option>

            <option value="Inactive">
              Inactive
            </option>

            <option value="Draft">
              Draft
            </option>
          </select>
        </div>
      </form>
    </RightDrawer>
  );
};

export default EditEmploymentDrawer;